import React, { useEffect, useMemo, useState } from "react";
import { Phone, Mail, Globe, MapPin, Clock, AlertCircle, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import SEO from "../components/SEO";

const CATEGORY_LABELS = {
    "crisis-hotline": "Crisis hotlines",
    "medical": "Medical",
    "mental-health": "Mental health",
    "safety-classes": "Safety classes",
    "education": "Education",
    "legal": "Legal aid",
    "family-services": "Family services",
    "food-assistance": "Food assistance",
    "veterans": "Veterans",
    "addiction-recovery": "Addiction recovery",
    "other": "Other",
};

const DirectoryCard = ({ entry }) => (
    <article
        data-testid={`directory-card-${entry.id}`}
        className={`bg-white rounded-2xl p-6 flex flex-col transition ${entry.is_emergency ? "border-2 border-[#E53935] shadow-md" : "border border-black/10 hover:border-[#E53935]/40"}`}
    >
        <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">{CATEGORY_LABELS[entry.category] || entry.category}</p>
                <h3 className="font-heading text-xl text-[#1C252E] leading-tight mt-1">{entry.name}</h3>
            </div>
            {entry.is_emergency && (
                <span className="shrink-0 text-[10px] uppercase tracking-wider font-bold bg-[#E53935] text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" strokeWidth={2.5} /> Crisis
                </span>
            )}
        </div>
        {entry.description && <p className="text-sm text-[#4A5568] mb-3 line-clamp-3">{entry.description}</p>}
        <div className="space-y-1.5 text-sm mt-auto pt-3 border-t border-black/5">
            {entry.phone && (
                <a href={`tel:${entry.phone}`} className="flex items-center gap-2 text-[#1C252E] hover:text-[#E53935]">
                    <Phone className="w-3.5 h-3.5 text-[#E53935] shrink-0" strokeWidth={1.5} />
                    <span className="font-medium">{entry.phone}</span>
                </a>
            )}
            {entry.email && (
                <a href={`mailto:${entry.email}`} className="flex items-center gap-2 text-[#4A5568] hover:text-[#E53935] break-all">
                    <Mail className="w-3.5 h-3.5 text-[#E53935] shrink-0" strokeWidth={1.5} /> {entry.email}
                </a>
            )}
            {entry.website && (
                <a href={entry.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#4A5568] hover:text-[#E53935] break-all">
                    <Globe className="w-3.5 h-3.5 text-[#E53935] shrink-0" strokeWidth={1.5} /> {entry.website.replace(/^https?:\/\//, "")}
                </a>
            )}
            {entry.address && (
                <p className="flex items-start gap-2 text-[#4A5568]">
                    <MapPin className="w-3.5 h-3.5 text-[#E53935] shrink-0 mt-0.5" strokeWidth={1.5} /> {entry.address}
                </p>
            )}
            {entry.hours && (
                <p className="flex items-start gap-2 text-[#4A5568]">
                    <Clock className="w-3.5 h-3.5 text-[#E53935] shrink-0 mt-0.5" strokeWidth={1.5} /> {entry.hours}
                </p>
            )}
        </div>
    </article>
);

const Directory = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("");

    useEffect(() => {
        api.get("/directory", { params: category ? { category } : {} })
            .then((r) => setRows(r.data || []))
            .catch(() => toast.error("Could not load directory"))
            .finally(() => setLoading(false));
    }, [category]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) =>
            [r.name, r.description, r.phone, r.address, r.tags?.join(" ")].filter(Boolean).some((f) => f.toLowerCase().includes(q))
        );
    }, [rows, search]);

    const emergencyRows = filtered.filter((r) => r.is_emergency);
    const regularRows = filtered.filter((r) => !r.is_emergency);

    return (
        <div className="min-h-screen bg-[#FDFBF7]">
            <SEO title="Community directory" description="Local resources — crisis hotlines, doctors, mental health, safety classes, legal aid, and more." path="/directory" />
            <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
                <header className="mb-10">
                    <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">Help when you need it</p>
                    <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium tracking-tight leading-[1.05]">Community directory</h1>
                    <p className="text-base text-[#4A5568] mt-4 max-w-2xl">A curated list of trusted contacts our board has vetted — crisis lines that pick up at 3 a.m., doctors taking new patients, certified safety instructors, legal aid, and more.</p>
                </header>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3 mb-8 flex-wrap">
                    <div className="relative flex-1 max-w-md">
                        <Search className="w-4 h-4 text-[#4A5568] absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                        <input
                            type="search"
                            placeholder="Search by name, phone, address…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid="directory-search-input"
                            className="w-full rounded-full bg-white border border-black/10 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                        />
                    </div>
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        data-testid="directory-category-select"
                        className="rounded-full border border-black/10 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                    >
                        <option value="">All categories</option>
                        {Object.entries(CATEGORY_LABELS).map(([slug, label]) => (
                            <option key={slug} value={slug}>{label}</option>
                        ))}
                    </select>
                </div>

                {loading ? (
                    <p className="text-sm text-[#4A5568]">Loading…</p>
                ) : filtered.length === 0 ? (
                    <div className="bg-white border border-black/10 rounded-2xl p-12 text-center" data-testid="directory-empty">
                        <Phone className="w-12 h-12 text-[#4A5568] mx-auto mb-3" strokeWidth={1.2} />
                        <p className="font-heading text-xl text-[#1C252E]">No matching entries yet.</p>
                        <p className="text-sm text-[#4A5568] mt-2">The board is building this list — check back soon.</p>
                    </div>
                ) : (
                    <>
                        {emergencyRows.length > 0 && (
                            <section className="mb-10" data-testid="directory-emergency-section">
                                <h2 className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-4">If you need help right now</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {emergencyRows.map((e) => <DirectoryCard key={e.id} entry={e} />)}
                                </div>
                            </section>
                        )}
                        {regularRows.length > 0 && (
                            <section data-testid="directory-list">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {regularRows.map((e) => <DirectoryCard key={e.id} entry={e} />)}
                                </div>
                            </section>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Directory;
